import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import api from './api';

const AuthContext = createContext(null);

// Decode JWT to get expiration time
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    try {
      const res = await api.post('/api/auth/refresh', { refresh_token: refreshToken });
      const { access_token, refresh_token: newRefreshToken, user: userData } = res.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      // Schedule next refresh
      scheduleTokenRefresh(access_token, newRefreshToken);
      
      return true;
    } catch (err) {
      // If refresh fails, clear tokens and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      return false;
    }
  }, []);

  // Schedule token refresh 5 minutes before expiration
  const scheduleTokenRefresh = useCallback((token, refreshToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    if (!token || !refreshToken) return;

    const decoded = decodeToken(token);
    if (!decoded?.exp) return;

    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    
    // Refresh 5 minutes before expiration (or immediately if already expired)
    const refreshTime = Math.max(0, timeUntilExpiry - (5 * 60 * 1000));

    refreshTimerRef.current = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  }, [refreshAccessToken]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Set up token refresh timer
        scheduleTokenRefresh(token, refreshToken);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  const login = useCallback(async (email, password, turnstileToken) => {
    try {
      const res = await api.post('/api/auth/login', {
        email,
        password,
        turnstile_token: turnstileToken || undefined,
      });
      const { access_token, refresh_token, user: userData } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      scheduleTokenRefresh(access_token, refresh_token);
      return { success: true, ...userData, roles: userData.roles };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Login failed' };
    }
  }, [scheduleTokenRefresh]);

  const register = useCallback(async (data) => {
    const payload = { ...data };
    if (payload.turnstile_token === undefined) delete payload.turnstile_token;
    const res = await api.post('/api/auth/register', payload);
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    scheduleTokenRefresh(access_token, refresh_token);
    return userData;
  }, [scheduleTokenRefresh]);

  const socialAuth = useCallback(async (credential, provider) => {
    const res = await api.post('/api/auth/social', { credential, provider });
    const { access_token, refresh_token, user: userData, is_new } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    scheduleTokenRefresh(access_token, refresh_token);
    return { ...userData, is_new };
  }, [scheduleTokenRefresh]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      const userData = res.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      return null;
    }
  }, []);

  const updateUser = useCallback((updatedFields) => {
    setUser(prev => {
      const updated = { ...prev, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const logout = useCallback(async () => {
    // SECURITY FIX (F-LOGOUT): Call backend logout API to revoke refresh token
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (refreshToken) {
      try {
        // Call backend logout endpoint to revoke the refresh token
        await api.post('/api/auth/logout', { refresh_token: refreshToken });
        console.log('Logout: Refresh token revoked on server');
      } catch (err) {
        // Log error but continue with client-side cleanup
        console.error('Logout API error (continuing with cleanup):', err.message);
      }
    }
    
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Clear all auth data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email, turnstileToken) => {
    await api.post('/api/auth/forgot-password', {
      email,
      turnstile_token: turnstileToken || undefined,
    });
    return { success: true };
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    const res = await api.post('/api/auth/reset-password', { token, new_password: newPassword });
    return { success: true, message: res.data.message };
  }, []);

  const isAdmin = user?.roles?.some(r => ['super_admin', 'admin', 'relationship_manager', 'finance', 'compliance', 'accounts_team'].includes(r));
  const isPartner = user?.roles?.some(r => ['partner_admin', 'partner_user'].includes(r));

  const contextValue = useMemo(() => ({ 
    user, 
    setUser, 
    loading, 
    login, 
    register, 
    socialAuth, 
    logout, 
    refreshUser, 
    updateUser, 
    forgotPassword, 
    resetPassword, 
    refreshAccessToken,
    isAdmin, 
    isPartner 
  }), [user, loading, login, register, socialAuth, logout, refreshUser, updateUser, forgotPassword, resetPassword, refreshAccessToken, isAdmin, isPartner]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
